/**
 * How applying a coupon ended, once add-coupon itself succeeded.
 *
 * - "applied": the order was repriced and re-read.
 * - "refetched": repricing or the re-read failed, but a refetch of the order
 *   succeeded, so the screen shows what the server holds.
 * - "stale": both failed; the coupon is on the order but the total on screen
 *   may be old, so the caller asks the customer to try again.
 */
export type CouponApplyOutcome = "applied" | "refetched" | "stale";

/**
 * Apply a coupon in its three requests, with only the first able to refuse it.
 *
 * add-coupon is the server's decision: if it throws, the code was refused
 * and that error propagates to the coupon field. Once it succeeds the coupon
 * IS on the order, so a later timeout or 5xx from calculate-amount or the
 * re-read must never read as "invalid coupon" — that told customers a code
 * the server had accepted was refused, beside the pre-coupon total (review
 * I3). Instead the coupon stays applied and the order is refetched.
 */
export async function applyCouponToOrder(steps: {
  /** add-coupon. A rejection is a refusal and propagates. */
  add: () => Promise<unknown>;
  /** Show the coupon as applied (context state). */
  markApplied: () => void;
  /** calculate-amount, then re-read the order into the cache. */
  refresh: () => Promise<unknown>;
  /** Fallback when refresh fails: refetch the order query. */
  refetch: () => Promise<unknown>;
}): Promise<CouponApplyOutcome> {
  await steps.add();
  steps.markApplied();
  try {
    await steps.refresh();
    return "applied";
  } catch {
    try {
      await steps.refetch();
      return "refetched";
    } catch {
      return "stale";
    }
  }
}
